import React, { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { RealestateAccessList } from "src/screens/dashboard/users/user/access/RealestateAccessList";
import { TenantAccessList } from "src/screens/dashboard/users/user/access/TenantAccessList";
import styles from "./UserAccess.module.css";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

interface Params {
    customerid: string;
    userid: string;
}

const UserAccessBase = () => {
    const { t } = useTranslation();
    const { customerid, userid } = useParams<Params>();
    const { navStore, userAccessStore, tenantAccessStore } = useContext(RootStoreContext);
    const [activeTab, setActiveTab] = useState("realestate-access");

    useEffect(() => {
        const title = userAccessStore.userDisplayName ?? t("labels.access");
        navStore.setTitle(title);
        navStore.setBackbutton(`/users/${customerid}/${userid}`, t("screens.dashboard.users.access.user_details_back"));
    }, [navStore, userAccessStore.userDisplayName, customerid, userid, t]);

    useEffect(() => {
        userAccessStore.load(customerid, userid);
    }, [userAccessStore, customerid, userid]);

    useEffect(() => {
        if (activeTab !== "tenant-access") {
            return;
        }

        void tenantAccessStore.load(customerid, userid).catch(() => undefined);
    }, [activeTab, tenantAccessStore, customerid, userid]);

    const tabs = [
        {
            key: "realestate-access",
            label: t("screens.dashboard.users.access.realestate_access_tab")
        },
        {
            key: "tenant-access",
            label: t("screens.dashboard.users.access.tenant_access_tab")
        }
    ];
    const realestateListIdentifier = `user-access-realestate-${customerid}-${userid}`;
    const tenantListIdentifier = `user-access-tenant-${customerid}-${userid}`;

    const handleTabChange = useCallback((tabKey: string) => {
        setActiveTab(tabKey);
    }, []);

    if (userAccessStore.isAccessLoading) {
        return <Screens.Loading />;
    }
    if (userAccessStore.accessError) {
        return <Screens.Error message={userAccessStore.accessError} />;
    }

    return (
        <React.Fragment>
            <UI.Container fluid className="pt-4">
                <UI.Row className="justify-content-md-center">
                    <UI.Col xs={12} sm={12} md={12} lg={11} xl={10}>
                        <UI.Card>
                            <div className={styles.tabsSpacer}>
                                <UI.Tabs
                                    selectedId={activeTab}
                                    onTabChange={handleTabChange}
                                    contents={tabs.map((tab) => ({
                                        id: tab.key,
                                        label: tab.label
                                    }))}
                                />
                            </div>
                            {activeTab === "realestate-access" && (
                                <RealestateAccessList listIdentifier={realestateListIdentifier} />
                            )}
                            {activeTab === "tenant-access" && (
                                <TenantAccessList listIdentifier={tenantListIdentifier} />
                            )}
                        </UI.Card>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </React.Fragment>
    );
};

export const UserAccess = Sentry.withProfiler(observer(UserAccessBase));
